
import { useContext, useState, useMemo, useEffect } from "react";
import { ADModalBody, ADModalFooter, ADModalInfo } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup, GlassLayerSelect, GlassOption, GlassSelect, GlassTextInputTransformer } from "./GlassInput";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { MapitStateContext } from "src/states/MapitState";
import { Localization } from "@viamap/viamap2-common";
import type { Polygon, MultiPolygon, Feature, Position } from "geojson";
import union from "@turf/union";
import { featureCollection } from "@turf/helpers";
import intersect from "@turf/intersect";
import { angle, difference, distance } from "@turf/turf";
import { addPreviewLayer, removePreviewLayer } from "src/utils/mapLayerHelpers";
import { GeoJSONSource } from "maplibre-gl";
import { GlassButton } from "./GlassButtons";
import { Persistence } from "src/managers/Persistence";
import { DK, EN, TranslationInline } from "./MitGlassComponents";

export function AreaFunctions({ window }: { window: ManagedWindow }) {
   let { dispatch: windowDispatch } = useWindowContext();
   let { state: mapitState } = useContext(MapitStateContext);

   const selectableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter(layer =>
         (layer.geoJson?.features[0].geometry?.type === "Polygon" ||
            layer.geoJson?.features[0].geometry?.type === "MultiPolygon") &&
         layer.visible === true
      ) || [];
   }, [mapitState.layers])

   let [layer1, setLayer1] = useState<number | null>(null);
   let [layer2, setLayer2] = useState<number | null>(null);

   let [operation, setOperation] = useState<string | null>(null);

   const funData = useMemo(() => {
      if (layer1 === null || layer2 === null || operation === null) return null;
      const funData = calcFunction(
         mapitState.layers[layer1!].geoJson as geo,
         mapitState.layers[layer2!].geoJson as geo,
         operation
      );
      console.log("Calculated function data:", funData);
      return funData ?? null;
   }, [mapitState.layers, layer1, layer2, operation]);

   useEffect(() => {
      try {
         if (!funData) return;
         const previewID = "layerFunPreview2";
         let map = mapitState.map?.getMapPop();
         addPreviewLayer(map!, previewID, "purple", "pink");
         (map.getSource(previewID) as GeoJSONSource)?.setData(funData as any);
         return () => {
            removePreviewLayer(map!, previewID);
         }
      } catch (e) {
         console.error("Error performing boolean operation:", e);
         return
      }
   }, [funData])


   function selectLayer1(e: number) {
      setLayer1(e);
   }

   function selectLayer2(e: number) {
      setLayer2(e);
   }

   function saveAsLayer() {
      if (layer1 === null || layer2 === null || operation === null) return;
      try {
         if (funData) {
            Persistence.createLayersFromGeoJSON(Localization.getText("Polygon functions"), featureCollection([funData]), new Date(), (layer) => {
               windowDispatch(openWindow(WindowId.NameOnCreation, { layers: layer }))
               windowDispatch(closeWindow(window.id));
            });
         } else {
            console.warn("No data generated from boolean operation.");
         }
      } catch (e) {
         console.error("Error saving boolean operation as layer:", e);
      }
   }



   return (
      <>
         <ADModalInfo>
            <TranslationInline>
               <DK>Vælg de områdelag, og den operation du vil udføre på dem.</DK>
               <EN>Select the area layers and the operation you want to perform on them.</EN>
            </TranslationInline>
         </ADModalInfo>
         <ADModalBody>
            <GlassInputGroup

            >
               <GlassTextInputTransformer label={Localization.getFormattedText("Area {symbol}", { symbol: "A" })}>
                  <GlassLayerSelect
                     selected={layer1 ?? undefined}
                     onSelect={selectLayer1}
                     layers={selectableLayers}
                  />
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getFormattedText("Area {symbol}", { symbol: "B" })}>
                  <GlassLayerSelect
                     selected={layer2 ?? undefined}
                     onSelect={selectLayer2}
                     layers={selectableLayers}
                  />
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Operation")}>
                  <GlassInputControl>
                     <GlassSelect<string>
                        value={operation ?? undefined}
                        onSelect={setOperation}
                        placeholder={Localization.getText("Select operation...")}
                     >
                        {polygonOperations.map(op => (
                           <GlassOption key={op.value} value={op.value}>
                              <span style={{ color: "purple", marginRight: "4px", backgroundColor: "white", borderRadius: "4px", minWidth: "58px", textAlign: "center", display: "block", float: "left" }}>{op.fun}</span>
                              {Localization.getText(op.fun)}
                           </GlassOption>
                        ))}
                     </GlassSelect>
                  </GlassInputControl>
               </GlassTextInputTransformer>
            </GlassInputGroup>
            <div style={{ color: "#770000", marginTop: "8px", fontSize: "12px", textAlign: "right", textWrap: "balance", marginRight: "12px" }}>
               {(!funData && operation && layer1 && layer2) &&
                  <TranslationInline>
                     <EN>No valid geometry was returned. Please try again.</EN>
                     <DK>Der blev ikke returneret nogen geometri. Prøv venligtst igen.</DK>
                  </TranslationInline>
               }
            </div>
         </ADModalBody>
         <ADModalFooter>
            <GlassButton disabled={!funData} onClick={() => saveAsLayer()}>{Localization.getText("Create layer")}</GlassButton>
         </ADModalFooter>
      </>
   )
}

type geo = GeoJSON.FeatureCollection<Polygon | MultiPolygon>;
function safeUnion(geo: geo) {
   if (geo.features.length === 0) return null;
   if (geo.features.length === 1) return geo.features[0];
   return union(geo);
}

function removeSlivers(coord: Position[]) {
   let copy = structuredClone(coord)
   let repeat = true;
   while (repeat) {
      repeat = false;
      for (let i = 0; i < copy.length; i++) {
         let pre = copy.at(i - 1);
         if (i === 0) pre = copy.at(copy.length - 2);
         let current = copy.at(i);
         let next = copy.at((i + 1) % copy.length);

         if (!pre || !next || !current) continue;
         let angleOfCurrent = angle(pre, current, next);
         let dist = distance(pre, current);
         let toleranceAngle = 1;
         let toleranceDistance = 0.00001; // approx 1 cm
         if (toleranceAngle >= angleOfCurrent || angleOfCurrent >= 360 - toleranceAngle || dist <= toleranceDistance) {
            if (i == 0) {
               copy.pop();
               copy.push(copy[1]);
            }
            copy.splice(i, 1);
            repeat = true;
            i--;
         }
      }
   }
   if (copy.length <= 3) return [];
   return copy;
}

function applyTolerance(geo: Feature<Polygon | MultiPolygon> | null) {
   if (!geo) return null;
   if (geo.geometry.type === "Polygon") {
      geo.geometry.coordinates = geo.geometry.coordinates.map(removeSlivers);
      if (geo.geometry.coordinates.length === 1 && geo.geometry.coordinates[0].length < 4) return null;
      return geo;
   }
   geo.geometry.coordinates = geo.geometry.coordinates.map((a) => a.map(removeSlivers)).filter(a => a.length > 0 && (a?.at(0)?.length ?? 0) >= 4);
   if (geo.geometry.coordinates.length === 0) return null;
   if (geo.geometry.coordinates.length === 1 && geo.geometry.coordinates[0].length < 4) return null;
   return geo;
}

function calcFunction(a: geo, b: geo, operation: typeof polygonOperations[number]["value"]) {
   switch (operation) {
      case "or":
         return applyTolerance(union(featureCollection([...a.features, ...b.features])));
      case "and": {
         const ua = safeUnion(a);
         const ub = safeUnion(b);
         if (!ua || !ub) return null;
         return applyTolerance(intersect(featureCollection([ua, ub])));
      }
      case "difference": {
         const ua = safeUnion(a);
         const ub = safeUnion(b);
         if (!ua || !ub) return null;
         return applyTolerance(difference(featureCollection([ua, ub])));
      }
      case "differenceInverse": {
         const ua = safeUnion(a);
         const ub = safeUnion(b);
         if (!ua || !ub) return null;
         return applyTolerance(difference(featureCollection([ub, ua])));
      }
      case "exclusive": {
         const uBoth = safeUnion(featureCollection([...a.features, ...b.features]))
         const uA = safeUnion(a);
         const uB = safeUnion(b);
         if (!uA || !uB) return null;
         const IBoth = intersect(featureCollection([uA, uB]));
         if (!IBoth) return uBoth;
         if (!uBoth) return null;
         return applyTolerance(difference(featureCollection([uBoth, IBoth])));
      }
   }
}

const polygonOperations = [
   { fun: "A + B", value: "or" },
   { fun: "A & B", value: "and" },
   { fun: "A - B", value: "difference" },
   { fun: "B - A", value: "differenceInverse" },
   { fun: "A ⊕ B", value: "exclusive" }
]